import { getRecordFormat } from '@adhd/shared';

/**
 * One thought record, printed as a broadsheet page.
 *
 * Chosen from four designs by printing them and looking. The one it replaced
 * was correct and dull — a single type size, weight and grey — and the fix is
 * not more ink but real contrast: a serif nameplate against 6pt apparatus,
 * three rule weights, a numbered rail, and the belief shift set at the size of
 * the thing it actually is, which is the outcome of the whole exercise.
 *
 * Up to five numbered sections in the order the work happens. This numbering is
 * the rail — the steps of one record — and is a different thing from the circled
 * per-cell numbers an earlier landscape version needed to say which of three
 * records a box belonged to. Those were noise; these are the method.
 *
 * "Up to" because a section with nothing in it is not printed, and the numbers
 * count what is on the page rather than each section's fixed place in the
 * design. A record with no belief rating and no emotions is numbered 01 to 04,
 * not 01, 02, 04, 05.
 */
export interface SheetRecord {
  occurredAt: string;
  format: string;
  situation: string;
  automaticThought: string;
  beliefBefore: number | null;
  beliefAfter: number | null;
  emotions: readonly { emotion: string; intensityBefore: number; intensityAfter: number | null }[];
  /**
   * Doses logged on the same day. Optional because the shared payload is
   * assembled separately and an older server will not send it — a printed sheet
   * must not break because one field is absent.
   */
  medications?: readonly {
    name: string;
    doseAmount: number | null;
    doseUnit: string;
    status: string;
    at: string | null;
    feelings: readonly string[];
  }[];
  evidence: readonly { side: 'for' | 'against'; text: string }[];
  distortions: readonly string[];
  /**
   * The traps the app picked when it drafted the worksheet from a journal
   * entry. Optional: an older server sends none, and then every trap reads as
   * his, as it always did.
   */
  appDistortions?: readonly string[];
  response: string | null;
  isComplete: boolean;
}

interface Props {
  record: SheetRecord;
  /**
   * The owner's timezone, where the reader may not be the owner.
   *
   * Only the shared view passes it. On the owner's own Export the two are the
   * same person and the browser is already right — but a clinician opening a
   * share link is somewhere else by definition, and that is the one page the
   * owner cannot preview before somebody else reads it.
   */
  timezone?: string | null;
}

/**
 * Two fit in the slab beside the belief figures without making the row taller.
 * The design arrived with room for exactly one, which the data model has never
 * matched — a check-in can name several, and the one that moved most is often
 * not the first. A third would cost height, so the rest are counted rather than
 * dropped: a sheet that quietly omits a feeling somebody rated is the kind of
 * loss its reader cannot detect.
 */
const FEELINGS_SHOWN = 2;

export function RecordSheet({ record, timezone }: Props): JSX.Element {
  const format = getRecordFormat(record.format);
  const situation = record.situation.trim();
  const thought = record.automaticThought.trim();
  const response = (record.response ?? '').trim();
  const forItems = record.evidence.filter((e) => e.side === 'for');
  const againstItems = record.evidence.filter((e) => e.side === 'against');
  const shown = record.emotions.slice(0, FEELINGS_SHOWN);
  const hidden = record.emotions.length - shown.length;

  // Same conditions as the sections below, in the order they render. Kept
  // adjacent to them: if a guard here and a guard there ever disagree, the sheet
  // silently renumbers, which is the failure this exists to prevent.
  const rendered = [
    situation ? 'situation' : null,
    thought ? 'thought' : null,
    record.beliefBefore !== null || shown.length > 0 ? 'numbers' : null,
    forItems.length > 0 || againstItems.length > 0 ? 'evidence' : null,
    'response',
  ].filter((k): k is string => k !== null);
  const step = (key: string): string => String(rendered.indexOf(key) + 1).padStart(2, '0');

  return (
    <article className="bs">
      <header className="bs-plate">
        <div>
          {/* The masthead is the constant and the worksheet is the edition.
              It used to print the format's NAME here, and one of them is
              "Spiralling about what might happen" — 34 characters at 26pt
              uppercase serif, three lines of masthead before any content. As a
              kicker it costs one 6.6pt line, and it holds for any format added
              later rather than fitting whichever name is longest today. */}
          <p className="bs-kicker">
            <i>Worksheet</i>
            <s>·</s>
            {format.name}
          </p>
          <h2>Thought record</h2>
        </div>
        <div className="bs-plate-meta">
          ADHD &amp; Me
          <span>Cognitive</span>
          <span>behavioural worksheet</span>
        </div>
      </header>
      <div className="bs-rule-heavy" />
      <div className="bs-folio">
        <span>Situation · Evidence · Response</span>
        <em>{record.isComplete ? 'Completed' : 'Not finished'}</em>
        <span>{formatDate(record.occurredAt)}</span>
      </div>
      <div className="bs-rule-hair" />

      {/* Numbered by what is actually printed, not by each section's place in
          the design. Every section here is conditional, and the numbers used to
          be hardcoded — so a record with no belief rating and no emotions came
          out as 01, 02, 04, 05, with a hole where step three would have been.

          On a sheet handed to a clinician that gap is not cosmetic. A numbered
          document that skips a number reads as a page with something removed
          from it, and the one thing this worksheet must never suggest is that
          it is showing less than it has. */}
      <div className="bs-body">
        {situation && (
          <Row n={step('situation')} label={format.labels.situation} sub={sub(format, 'situation')}>
            <p className="bs-lede">{situation}</p>
          </Row>
        )}

        {thought && (
          <Row n={step('thought')} label={format.labels.thought} sub="Verbatim">
            <div className="bs-quote">
              <span className="bs-qm" aria-hidden="true">
                “
              </span>
              <p className="bs-thought">{thought}”</p>
            </div>
            <p className="bs-credit">Automatic thought · caught at the time</p>
          </Row>
        )}

        {(record.beliefBefore !== null || shown.length > 0) && (
          <Row n={step('numbers')} label="The numbers" sub="Before / after" major>
            <div className="bs-stats">
            {/* Drawn rather than filled: see .bs-slab-fill. A background is at
                the mercy of the print dialog's "Background graphics" switch; an
                SVG rect is content and prints regardless.

                The rect carries no fill attribute on purpose — it is set from
                CSS. `var()` inside an SVG presentation attribute resolves in
                Chrome and silently does not in Safari, which left the rect
                unpainted on exactly the machine this gets printed from, while
                every check I ran in Chrome said it was fine. */}
            <svg className="bs-slab-fill" preserveAspectRatio="none" aria-hidden="true">
              <rect width="100%" height="100%" />
            </svg>
              <div className="bs-stat">
                <div className="bs-cap">Belief in the thought</div>
                <div className="bs-stat-body">
                {record.beliefBefore === null ? (
                  <p className="bs-delta">Not rated</p>
                ) : (
                  <>
                    <div className="bs-figs">
                      <span className="bs-was">{record.beliefBefore}</span>
                      {record.beliefAfter !== null && (
                        <>
                          <span className="bs-arw" aria-hidden="true">
                            →
                          </span>
                          <span className="bs-now">
                            {record.beliefAfter}
                            <u>%</u>
                          </span>
                        </>
                      )}
                    </div>
                    {record.beliefAfter !== null && (
                      <p className="bs-delta">
                        {shiftWord(record.beliefBefore, record.beliefAfter)}
                      </p>
                    )}
                  </>
                )}
                </div>
              </div>

              <div className="bs-stat">
                <div className="bs-cap">
                  {shown.length === 1 ? 'Feeling' : 'Feelings'} <em>· before / after</em>
                </div>
                <div className="bs-stat-body">
                {shown.length === 0 ? (
                  <p className="bs-delta">None recorded</p>
                ) : (
                  <div className="bs-feelings">
                    {shown.map((e) => (
                      <div className="bs-feel" key={e.emotion}>
                        <span className="bs-feel-name">{e.emotion}</span>
                        {/* With the unit, which the worksheet layout has always
                            printed and this one never did — the same field, two
                            documents, and a clinician holding both. Settled in
                            favour of showing it: a bare 84 beside a belief
                            written as 84% invites the reading that they are
                            different kinds of number. */}
                        <span className="bs-feel-was">{e.intensityBefore}%</span>
                        {e.intensityAfter !== null && (
                          <>
                            <span className="bs-feel-arw" aria-hidden="true">
                              →
                            </span>
                            <span className="bs-feel-now">{e.intensityAfter}%</span>
                          </>
                        )}
                      </div>
                    ))}
                    {hidden > 0 && (
                      <span className="bs-more">
                        + {hidden} more {hidden === 1 ? 'feeling' : 'feelings'} recorded
                      </span>
                    )}
                  </div>
                )}
                </div>
              </div>
            </div>
          </Row>
        )}

        {(forItems.length > 0 || againstItems.length > 0) && (
          <Row
            n={step('evidence')}
            label="The evidence"
            sub={forItems.length > 0 && againstItems.length > 0 ? 'Both sides' : 'Weigh it up'}
          >
            <div
              className={
                forItems.length > 0 && againstItems.length > 0
                  ? 'bs-evid'
                  : 'bs-evid bs-evid-single'
              }
            >
              {forItems.length > 0 && (
              <div>
                <span className="bs-chip">{format.labels.evidenceFor}</span>
                <ol>
                  {forItems.map((e, i) => (
                    <li key={i}>
                      <i>{i + 1}</i>
                      <span>{e.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
              )}
              {againstItems.length > 0 && (
              <div>
                <span className="bs-chip bs-chip-out">{format.labels.evidenceAgainst}</span>
                <ol>
                  {againstItems.map((e, i) => (
                    <li key={i}>
                      <i>{i + 1}</i>
                      <span>{e.text}</span>
                    </li>
                  ))}
                </ol>
              </div>
              )}
            </div>
          </Row>
        )}

        <Row n={step('response')} label={format.labels.response} sub={sub(format, 'response')} major>
          {response ? (
            <div className="bs-resp">
              <div className="bs-resp-bar" aria-hidden="true" />
              <p>{response}</p>
            </div>
          ) : (
            /* Named rather than left blank. An unfinished record is a thought
               written down and never answered, which is the thing worth
               talking about — a sheet that simply stops reads as a misprint. */
            <p className="bs-unfinished">Left blank — this record was never finished</p>
          )}
        </Row>
      </div>

      {/* Context on the day, printed under the record rather than inside it:
          it is not one of the worksheet's steps and must not read as one. There
          is roughly 107mm of spare page under a record, so one line per
          medication costs nothing that was being used. */}
      {record.medications && record.medications.length > 0 && (
        <div className="bs-meds">
          <span className="bs-meds-label">Medication that day</span>
          <span>
            {record.medications
              .map((m) => {
                const dose =
                  m.doseAmount === null
                    ? ''
                    : ` ${m.doseAmount}${m.doseUnit === 'other' ? '' : ` ${m.doseUnit}`}`;
                // Named outright, same as on screen. On paper especially, a
                // reader cannot ask what "not taken" meant — forgotten and
                // deliberately held back are different clinical facts and the
                // record already knows which.
                /*
                 * The owner's clock, not the reader's.
                 *
                 * `m.at` is a UTC instant and this had no `timeZone`, so it
                 * rendered wherever the page happened to be open. Every other
                 * time on this sheet is pinned to the owner already; this one
                 * printed a Honolulu owner's 9am dose as 3pm to a mainland
                 * clinician — on the one page the owner cannot preview before
                 * somebody else reads it, and where the whole reason to print
                 * a dose is its timing relative to the thought.
                 */
                const time = m.at
                  ? new Date(m.at).toLocaleTimeString(undefined, {
                      hour: 'numeric',
                      minute: '2-digit',
                      ...(timezone ? { timeZone: timezone } : {}),
                    })
                  : null;
                const state =
                  m.status === 'missed'
                    ? ' — MISSED'
                    : m.status === 'skipped'
                      ? ' — SKIPPED'
                      : m.status === 'late'
                        ? ` — taken late${time ? `, ${time}` : ''}`
                        : ` — taken${time ? `, ${time}` : ''}`;
                const feel = m.feelings.length > 0 ? ` — ${m.feelings.join(', ')}` : '';
                return `${m.name}${dose}${state}${feel}`;
              })
              .join(' · ')}
          </span>
        </div>
      )}

      <div className="bs-foot">
        <span>ADHD &amp; Me · Thought record · Private</span>
        {/* The app's picks, dashed and labelled in words too — a dashed
            underline does not survive every printer. */}
        {record.distortions.length > 0 && (
          <em>
            {record.distortions.map((key, i) => {
              const app = (record.appDistortions ?? []).includes(key);
              // The separator outside the span, so only the app's trap is dashed.
              return (
                <span key={key}>
                  {i > 0 ? ' / ' : ''}
                  <span className={app ? 'bs-trap-app' : undefined}>
                    {humanise(key)}
                    {app ? ' (app)' : ''}
                  </span>
                </span>
              );
            })}
          </em>
        )}
      </div>
      {(record.appDistortions ?? []).some((k) => record.distortions.includes(k)) && (
        <p className="bs-trap-note">
          (APP) — a thinking trap the app picked when it drafted this worksheet from a journal entry, not one
          chosen by hand.
        </p>
      )}
    </article>
  );
}

function Row({
  n,
  label,
  sub,
  major,
  children,
}: {
  n: string;
  label: string;
  sub: string;
  major?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className={major ? 'bs-row bs-row-major' : 'bs-row'}>
      {/* Header line, rule, then content across the whole measure. The 22mm
          rail this replaces cost 28mm with its gutter — 16% of the page spent
          on five two-digit numbers. The numerals are tabular, so the labels
          beside them keep the straight left edge the rail gave for free. */}
      <div className="bs-head">
        <span className="bs-no">{n}</span>
        <span className="bs-lbl">
          {label}
          {sub && <em> · {sub.toLowerCase()}</em>}
        </span>
      </div>
      <div className="bs-sep" />
      {children}
    </section>
  );
}
/**
 * The small line under a rail label. Taken from the step's own short label,
 * which is a different string from the section heading — passing a literal
 * printed "WHAT HAPPENED / WHAT HAPPENED" on the situation rail.
 */
function sub(format: ReturnType<typeof getRecordFormat>, field: 'situation' | 'response'): string {
  const step = format.steps.find((st) => st.field === field);
  const label = step?.label ?? '';
  const heading = field === 'situation' ? format.labels.situation : format.labels.response;
  return label && label.toLowerCase() !== heading.toLowerCase() ? label : '';
}

/** "Down 50 points" reads as an outcome; "-50" reads as a lab value. */
function shiftWord(before: number, after: number): string {
  const delta = before - after;
  if (delta === 0) return 'No change';
  return `${delta > 0 ? 'Down' : 'Up'} ${Math.abs(delta)} points`;
}

/** mind_reading -> Mind reading. The keys are storage, not something to read. */
function humanise(key: string): string {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formatDate(iso: string): string {
  // A bare YYYY-MM-DD parses as midnight UTC and renders as the previous day
  // west of Greenwich, silently misdating every record together. Build it from
  // its parts instead — the share payload feeds exactly that form.
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const date = parts
    ? new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]))
    : new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Date not recorded';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}
